import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Save, ArrowLeft, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Spinner } from "../components/UI";
import { fileToBase64, watermarkImage } from "../lib/utils";

export default function VisitForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetEstab = params.get("establishment_id") || "";

  const [establishments, setEstablishments] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [form, setForm] = useState({
    establishment_id: presetEstab,
    visit_date: new Date().toISOString().slice(0, 16),
    notes: "",
    outcome: "",
    photos: [],
  });
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/establishments", { params: { limit: 500 } });
        setEstablishments(data.items || []);
        if (!form.establishment_id && data.items?.length === 1) {
          setForm((f) => ({ ...f, establishment_id: data.items[0].id }));
        }
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const next = [];
    const dateLabel = new Date(form.visit_date || Date.now()).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`"${f.name}" is over 10MB — skipped.`);
        continue;
      }
      // Stamp the Literally Spirits logo + visit date in the bottom-right.
      // Falls back to the raw file if watermarking fails.
      let dataUrl;
      try {
        dataUrl = await watermarkImage(f, dateLabel);
      } catch {
        dataUrl = await fileToBase64(f);
      }
      // Stable per-photo ID so React keys survive removal/reordering.
      next.push({ id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, data: dataUrl });
    }
    setForm((s) => ({ ...s, photos: [...s.photos, ...next] }));
    e.target.value = "";
  };

  const removePhoto = (id) => {
    setForm((s) => ({ ...s, photos: s.photos.filter((p) => p.id !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.establishment_id) { toast.error("Please choose an establishment."); return; }
    if (!form.notes.trim()) { toast.error("Notes are required."); return; }
    setSaving(true);
    try {
      const payload = {
        establishment_id: form.establishment_id,
        visit_date: new Date(form.visit_date).toISOString(),
        notes: form.notes,
        outcome: form.outcome,
        photos: form.photos.map((p) => p.data),
      };
      await api.post("/visits", payload);
      toast.success("Visit logged");
      navigate(`/establishments/${form.establishment_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loadingList) return <Spinner />;

  return (
    <div>
      <Link to="/visits" className="btn-ghost mb-2" data-testid="back-visits">
        <ArrowLeft size={16} /> Back to visits
      </Link>
      <PageHeader
        testid="visit-form-header"
        title="Log a visit"
        subtitle="Capture the conversation while it's fresh — notes, outcome, and a few photos."
      />

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-4" data-testid="visit-form">
        <div className="card p-6 lg:col-span-2 space-y-4">
          <div>
            <div className="label mb-1">Establishment</div>
            <select
              data-testid="visit-establishment"
              className="input-base"
              required
              value={form.establishment_id}
              onChange={(e) => setForm((f) => ({ ...f, establishment_id: e.target.value }))}
            >
              <option value="">— Pick one —</option>
              {establishments.map((e) => (
                <option key={e.id} value={e.id}>{e.name}{e.city ? ` — ${e.city}` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="label mb-1">Visit date & time</div>
            <input
              data-testid="visit-date"
              type="datetime-local"
              className="input-base"
              value={form.visit_date}
              onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
            />
          </div>

          <div>
            <div className="label mb-1">Notes</div>
            <textarea
              data-testid="visit-notes"
              className="input-base min-h-[140px]"
              required
              placeholder="Walked in around 6pm, talked to bar manager Sarah about the menu refresh…"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>

          <div>
            <div className="label mb-1">Outcome (optional)</div>
            <input
              data-testid="visit-outcome"
              className="input-base"
              placeholder="Sampled product · Sent proposal · Follow-up scheduled"
              value={form.outcome}
              onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-6">
            <div className="label mb-2">Photos</div>
            <label className="btn-outline w-full cursor-pointer" data-testid="upload-photos-trigger">
              <ImagePlus size={16} />
              Add photos
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onFiles}
                data-testid="visit-photos-input"
              />
            </label>
            <div className="text-xs text-brand-mutedFg mt-2">Up to 10MB each.</div>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3" data-testid="visit-photos-preview">
                {form.photos.map((p, i) => (
                  <div key={p.id} className="relative group">
                    <img src={p.data} alt={`preview ${i+1}`} className="w-full h-20 object-cover rounded-lg border border-brand-border" />
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      className="absolute -top-1 -right-1 bg-brand-ink text-white rounded-full w-6 h-6 flex items-center justify-center opacity-90 hover:opacity-100"
                      data-testid={`remove-photo-${p.id}`}
                      title="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="save-visit">
            <Save size={16} /> {saving ? "Saving…" : "Save visit"}
          </button>
        </div>
      </form>
    </div>
  );
}
